import React, { useState, useEffect, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw, Loader2 } from "lucide-react";
import { CustomDataTable } from "@/components/table/CustomDataTable";
import { KolDataWithDates } from '@/types/table';
import { DataQualityTableProps, updateKolTrackerRow, transformKolTrackerData, groupDataByPic } from './dataQualityUtils';
import { GroupedDataQualityTable } from './GroupedDataQualityTable';
import { EnhancedTableCell } from "@/components/table/EnhancedTableCell";
import { 
  LANGUAGE_OPTIONS, 
  PLATFORM_OPTIONS, 
  TYPE_OPTIONS 
} from '@/components/kol/constants/kolPostingLinksConstants';

export const OrganicFreeExpenseTable: React.FC<DataQualityTableProps> = ({ onDataChange, groupByPic = false }) => {
  const [data, setData] = useState<KolDataWithDates[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Get total count for records where expense = 0
      const { count } = await supabase
        .from('kol_tracker')
        .select('*', { count: 'exact', head: true })
        .eq('expense', 0);
      
      setTotalCount(count || 0);

      // Get all data without pagination
      const { data: rawData, error } = await supabase
        .from('kol_tracker')
        .select('*')
        .eq('expense', 0)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedData = transformKolTrackerData(rawData || []);
      setData(transformedData);
    } catch (error) {
      console.error('Error fetching organic/free expenses data:', error);
      toast({
        title: "Error",
        description: "Failed to load records with organic/free expenses (expense = 0)",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRowEdit = async (newRow: KolDataWithDates, oldRow: KolDataWithDates) => {
    try {
      setIsProcessing(true);
      
      const changedFields = Object.keys(newRow).filter(
        key => newRow[key as keyof KolDataWithDates] !== oldRow[key as keyof KolDataWithDates]
      );

      for (const field of changedFields) {
        await updateKolTrackerRow(
          newRow.id, 
          field as keyof KolDataWithDates, 
          newRow[field as keyof KolDataWithDates]
        );
      }

      toast({
        title: "Success",
        description: "Record updated successfully",
      });

      await fetchData();
      if (onDataChange) onDataChange();
      
      return newRow;
    } catch (error) {
      console.error("Error updating row:", error);
      toast({
        title: "Error",
        description: "Failed to update record",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const columns = useMemo(() => [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      editable: false,
      minWidth: 300,
      cell: (row: KolDataWithDates) => (
        <div className="font-mono text-xs text-muted-foreground" title={row.id}>
          {row.id}
        </div>
      )
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      editable: true,
      inputType: 'text' as const,
      minWidth: 150,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.name}
          field="name"
          rowData={row}
          editable={true}
          inputType="text"
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'expense',
      header: 'Expense',
      accessorKey: 'expense',
      editable: true,
      inputType: 'number' as const,
      minWidth: 100,
      cell: (row: KolDataWithDates) => (
        <div className="flex items-center gap-2">
          <span className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 px-2 py-1 rounded text-xs">
            Organic/Free (0)
          </span>
          <EnhancedTableCell
            value={row.expense}
            field="expense"
            rowData={row}
            editable={true}
            inputType="number"
            onChange={async (field, value) => {
              const updatedRow = { ...row, [field]: value };
              await handleRowEdit(updatedRow, row);
            }}
          />
        </div>
      )
    },
    {
      id: 'post_link',
      header: 'Post Link',
      accessorKey: 'post_link',
      editable: true,
      inputType: 'url' as const,
      minWidth: 200,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.post_link}
          field="post_link"
          rowData={row}
          editable={true}
          inputType="url"
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'platform',
      header: 'Platform',
      accessorKey: 'platform',
      editable: true,
      inputType: 'select' as const,
      selectOptions: PLATFORM_OPTIONS.map(platform => ({ label: platform, value: platform })),
      minWidth: 120,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.platform}
          field="platform"
          rowData={row}
          editable={true}
          inputType="select"
          selectOptions={PLATFORM_OPTIONS.map(platform => ({ label: platform, value: platform }))}
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      editable: true,
      inputType: 'select' as const,
      selectOptions: TYPE_OPTIONS.map(type => ({ label: type, value: type })),
      minWidth: 150,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.type}
          field="type"
          rowData={row}
          editable={true}
          inputType="select"
          selectOptions={TYPE_OPTIONS.map(type => ({ label: type, value: type }))}
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'channel',
      header: 'Channel',
      accessorKey: 'channel',
      editable: true,
      inputType: 'text' as const,
      minWidth: 150,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.channel}
          field="channel"
          rowData={row}
          editable={true}
          inputType="text"
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    }
  ], []);

  const groupedData = useMemo(() => {
    if (groupByPic) {
      return groupDataByPic(data);
    }
    return {};
  }, [data, groupByPic]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Organic and Free Expenses</h3>
          <p className="text-sm text-muted-foreground">
            {totalCount} record{totalCount !== 1 ? 's' : ''} found with organic/free expenses (expense = 0)
          </p>
        </div>
        <Button
          onClick={() => fetchData()}
          disabled={isLoading}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isProcessing && (
        <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
          <Loader2 className="h-3 w-3 animate-spin" />
          Saving changes...
        </div>
      )}

      {groupByPic ? (
        <GroupedDataQualityTable
          groupedData={groupedData}
          columns={columns}
          loading={isLoading}
          onRowEdit={handleRowEdit}
        />
      ) : (
        <CustomDataTable
          data={data}
          columns={columns}
          loading={isLoading}
          onRowEdit={handleRowEdit}
          enableColumnResizing={true}
          enableSorting={true}
          showPagination={false}
        />
      )}
    </div>
  );
};
